import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const InteractiveTimeline = () => {
  const [expandedMilestone, setExpandedMilestone] = useState(null);

  const milestones = [
    {
      id: 1,
      year: "2021",
      title: "Started Coding Journey",
      subtitle: "Self-taught Developer",
      description: "Began learning web development through online courses and building personal projects. Focused on HTML, CSS, and JavaScript fundamentals.",
      icon: "Code",
      color: "bg-blue-500",
      details: {
        achievements: [
          "Completed 15+ online courses",
          "Built first portfolio website",
          "Learned Git and version control",
          "Created 10+ practice projects"
        ],
        technologies: ["HTML", "CSS", "JavaScript", "Git", "Bootstrap"],
        image: "https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=400&h=250&fit=crop"
      }
    },
    {
      id: 2,
      year: "2023",
      title: "First Developer Role",
      subtitle: "Junior Frontend Developer",
      description: "Landed my first professional role at a startup, working on React applications and learning from senior developers.",
      icon: "Briefcase",
      color: "bg-emerald-500",
      details: {
        achievements: [
          "Built responsive web applications",
          "Collaborated with design team",
          "Implemented user authentication",
          "Optimized application performance"
        ],
        technologies: ["React", "Redux", "Node.js", "MongoDB", "Sass"],
        image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=250&fit=crop"
      }
    },
    // {
    //   id: 3,
    //   year: "2020",
    //   title: "Freelance Success",
    //   subtitle: "Independent Contractor",
    //   description: "Transitioned to freelancing, working with multiple clients on diverse projects and building a strong reputation.",
    //   icon: "Zap",
    //   color: "bg-purple-500",
    //   details: {
    //     achievements: [
    //       "Completed 25+ client projects",
    //       "Built e-commerce platforms",
    //       "Developed mobile applications",
    //       "Achieved 5-star client ratings"
    //     ],
    //     technologies: ["React Native", "Next.js", "TypeScript", "PostgreSQL", "AWS"],
    //     image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=250&fit=crop"
    //   }
    // },
    {
      id: 4,
      year: "2023",
      title: "Started Mentoring",
      subtitle: "Community Contributor",
      description: "Began mentoring junior developers and contributing to open-source projects. Discovered passion for teaching and community building.",
      icon: "Users",
      color: "bg-orange-500",
      details: {
        achievements: [
          "Mentored 50+ developers",
          "Contributed to 20+ open-source projects",
          "Spoke at 5 tech meetups",
          "Created educational content"
        ],
        technologies: ["Open Source", "Git", "Documentation", "Teaching"],
        image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&h=250&fit=crop"
      }
    },
    {
      id: 5,
      year: "2024",
      title: "IMAC Program Launch",
      subtitle: "Structured Mentorship",
      description: "Launched the IMAC (Inspire, Mentor, Achieve, Create) program to provide structured mentorship for career changers and junior developers.",
      icon: "Rocket",
      color: "bg-pink-500",
      details: {
        achievements: [
          "Launched IMAC program",
          "Helped 100+ career transitions",
          "Created curriculum and resources",
          "Built mentorship community"
        ],
        technologies: ["Curriculum Design", "Community Building", "Mentorship", "Career Guidance"],
        image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop"
      }
    },
    {
      id: 6,
      year: "2024",
      title: "CodersHub Founded",
      subtitle: "Community Platform",
      description: "Founded CodersHub, a comprehensive platform connecting developers worldwide for learning, collaboration, and career growth.",
      icon: "Globe",
      color: "bg-cyan-500",
      details: {
        achievements: [
          "Built CodersHub platform",
          "Reached 10,000+ members",
          "Organized 50+ events",
          "Facilitated 500+ connections"
        ],
        technologies: ["Community Platform", "Event Management", "Networking", "Global Reach"],
        image: "/images/projects/ch.png"
      }
    },
    {
      id: 7,
      year: "2025",
      title: "Scaling Impact",
      subtitle: "Global Recognition",
      description: "Expanded CodersHub to 5 Universities, recognized as a leading voice in developer education and community building.",
      icon: "Trophy",
      color: "bg-yellow-500",
      details: {
        achievements: [
          "1,000+ community members",
          "2 Schools represented",
          "200+ successful mentorships",
          "Industry recognition awards"
        ],
        technologies: ["Global Scaling", "Leadership", "Public Speaking", "Industry Recognition"],
        image: "/me.png"
      }
    }
  ];

  const toggleMilestone = (id) => {
    setExpandedMilestone(expandedMilestone === id ? null : id);
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Clock" size={20} className="text-primary" />
            <span className="text-sm font-brand-medium text-primary tracking-wide uppercase">My Journey</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-brand-headline text-foreground mb-6">
            From Code Beginner to
            <span className="block text-primary text-gradient-gold">Community Leader</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto font-brand-body">
            Every milestone represents growth, learning, and the expanding impact of combining 
            technical excellence with community building.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-primary opacity-30"></div>

          {/* Milestones */}
          <div className="space-y-8">
            {milestones?.map((milestone, index) => (
              <div
                key={milestone?.id}
                className={`relative flex items-start space-x-8 transition-all duration-500 ${
                  expandedMilestone === milestone?.id ? 'scale-105' : ''
                }`}
              >
                {/* Timeline Dot */}
                <div className="relative z-10 flex-shrink-0">
                  <div className={`w-16 h-16 ${milestone?.color} rounded-full flex items-center justify-center shadow-lg border-4 border-background`}>
                    <Icon name={milestone?.icon} size={24} className="text-white" />
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-background px-2 py-1 rounded text-xs font-brand-medium text-primary">
                    {milestone?.year}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div
                    className={`bg-card rounded-xl p-6 border border-border hover:border-primary/30 transition-all duration-300 cursor-pointer brand-shadow hover:brand-shadow-lg ${
                      expandedMilestone === milestone?.id ? 'border-primary/50 brand-shadow-lg' : ''
                    }`}
                    onClick={() => toggleMilestone(milestone?.id)}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-brand-subheading text-foreground mb-1">
                          {milestone?.title}
                        </h3>
                        <p className="text-sm text-primary font-brand-medium">
                          {milestone?.subtitle}
                        </p>
                      </div>
                      <Icon
                        name={expandedMilestone === milestone?.id ? "ChevronUp" : "ChevronDown"}
                        size={20}
                        className="text-text-secondary"
                      />
                    </div>

                    {/* Description */}
                    <p className="text-text-secondary font-brand-body mb-4">
                      {milestone?.description}
                    </p>

                    {/* Expanded Details */}
                    {expandedMilestone === milestone?.id && (
                      <div className="mt-6 pt-6 border-t border-border space-y-6 animate-in slide-in-from-top duration-300">
                        {/* Image */}
                        <div className="w-full h-48 rounded-lg overflow-hidden">
                          <Image
                            src={milestone?.details?.image}
                            alt={milestone?.title}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Achievements */}
                          <div>
                            <h4 className="text-sm font-brand-medium text-foreground mb-3 flex items-center">
                              <Icon name="CheckCircle" size={16} className="text-success mr-2" />
                              Key Achievements
                            </h4>
                            <ul className="space-y-2">
                              {milestone?.details?.achievements?.map((achievement, idx) => (
                                <li key={idx} className="text-sm text-text-secondary flex items-start">
                                  <Icon name="ArrowRight" size={14} className="text-primary mr-2 mt-0.5 flex-shrink-0" />
                                  {achievement}
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Technologies */}
                          <div>
                            <h4 className="text-sm font-brand-medium text-foreground mb-3 flex items-center">
                              <Icon name="Zap" size={16} className="text-accent mr-2" />
                              Technologies & Skills
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {milestone?.details?.technologies?.map((tech, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 bg-primary/10 text-primary text-xs font-brand-medium rounded-full border border-primary/20"
                                >
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Current Status */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-6 py-3 rounded-full border border-primary/20">
            <Icon name="Sparkles" size={20} />
            <span className="font-brand-medium">Currently building the future of developer communities</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveTimeline;